import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ArrowLeft, Calendar, MapPin, GraduationCap, Clock, Tag } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import settings from '../../constants/settings';
import { getBeasiswaBySlug } from '../../utils/beasiswa';

const BeasiswaDetail = () => {
  const { slug } = useParams();
  const [beasiswa, setBeasiswa] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchBeasiswaDetail = async () => {
      try {
        setIsLoading(true);
        const fetchedBeasiswa = await getBeasiswaBySlug(slug);
        
        if (fetchedBeasiswa) {
          setBeasiswa(fetchedBeasiswa);
        } else {
          setNotFound(true);
        }
      } catch (error) {
        console.error('Error fetching beasiswa detail:', error);
        setNotFound(true);
      } finally {
        setIsLoading(false);
      }
    };

    if (slug) {
      fetchBeasiswaDetail();
    }
  }, [slug]);

  // Format date to Indonesian format
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('id-ID', options);
  };

  // Format comma-separated values
  const formatCommaSeparated = (value) => {
    if (!value) return '';
    
    // If it's already an array, join with comma and space
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    
    // If it's a string that contains commas, split and rejoin with comma and space
    if (typeof value === 'string' && value.includes(',')) {
      return value.split(',').map(item => item.trim()).join(', ');
    }
    
    // Otherwise, return as is
    return value;
  };

  // Handle apply button click
  const handleApplyClick = () => {
    const consultantNumber = settings.wa_consultant_link.replace('https://wa.me/', '');
    const message = `Saya tertarik dengan beasiswa: ${beasiswa.title}`;
    window.open(`https://wa.me/${consultantNumber}?text=${encodeURIComponent(message)}`, '_blank');
  };

  // Redirect to beasiswa index if not found
  if (notFound) {
    return <Navigate to="/beasiswa" />;
  }

  if (isLoading) {
    return (
      <div className="pt-[90px] flex justify-center items-center min-h-[60vh]">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="pt-[90px] bg-white">
      <Helmet>
        <title>{beasiswa.title} | Pintar Pilih Kuliah</title>
        <meta name="description" content={beasiswa.excerpt} />
        {beasiswa.coverImage && (
          <meta property="og:image" content={beasiswa.coverImage} />
        )}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={beasiswa.title} />
        <meta property="og:description" content={beasiswa.excerpt} />
        <meta property="og:site_name" content={settings.app_name} />
      </Helmet>
      
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Link to="/beasiswa" className="inline-flex items-center text-gold hover:text-cream transition-colors mb-8">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Kembali ke Katalog Beasiswa
        </Link>
        
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="aspect-[16/9] overflow-hidden bg-gray-100">
            <img
              src={beasiswa.coverImage}
              alt={beasiswa.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = 'https://images.unsplash.com/photo-1546410531-bb4caa6b424d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1471&q=80';
              }}
            />
          </div>
          
          <div className="p-6 md:p-8">
            <h1 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-6">{beasiswa.title}</h1>
            
            {/* Tags */}
            {beasiswa.tags && beasiswa.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {Array.isArray(beasiswa.tags) ? beasiswa.tags.map((tag, index) => (
                  <span key={index} className="inline-flex items-center text-sm font-medium bg-cream/30 text-dark-navy px-2.5 py-0.5 rounded-full">
                    <Tag className="w-3 h-3 mr-1" />
                    {tag}
                  </span>
                )) : (
                  <span className="inline-flex items-center text-sm font-medium bg-cream/30 text-dark-navy px-2.5 py-0.5 rounded-full">
                    <Tag className="w-3 h-3 mr-1" />
                    {beasiswa.tags}
                  </span>
                )}
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <div className="flex items-center text-gray-600">
                <MapPin className="h-5 w-5 mr-2 text-gold" />
                <span>Negara: <strong>{formatCommaSeparated(beasiswa.country)}</strong></span>
              </div>
              <div className="flex items-center text-gray-600">
                <Clock className="h-5 w-5 mr-2 text-gold" />
                <span>Diposting: <strong>{formatDate(beasiswa.date)}</strong></span>
              </div>
              <div className="flex items-center text-gray-600">
                <GraduationCap className="h-5 w-5 mr-2 text-gold" />
                <span>Jenjang: <strong>{formatCommaSeparated(beasiswa.degree)}</strong></span>
              </div>
              <div className="flex items-center text-gray-600">
                <Calendar className="h-5 w-5 mr-2 text-gold" />
                <span>Deadline: <strong>{beasiswa.deadline ? (new Date(beasiswa.deadline).toString() === 'Invalid Date' ? beasiswa.deadline : formatDate(beasiswa.deadline)) : '-'}</strong></span>
              </div>
            </div>
            
            <div className="prose prose-lg max-w-none mb-8 markdown-content custom-markdown">
              <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                {beasiswa.content}
              </ReactMarkdown>
            </div>
            
            {/* <div className="bg-cream/20 p-6 rounded-lg mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Informasi Universitas</h2>
              <div className="mb-4">
                <h3 className="font-semibold text-gray-800">Universitas</h3>
                <p>{formatCommaSeparated(beasiswa.university)}</p>
              </div>
              {beasiswa.website && (
                <div>
                  <h3 className="font-semibold text-gray-800">Website Resmi</h3>
                  <a 
                    href={beasiswa.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gold hover:underline"
                  >
                    {beasiswa.website}
                  </a>
                </div>
              )}
            </div>
            
            <div className="flex justify-center">
              <button
                className="inline-flex items-center justify-center bg-orange-500 hover:bg-black text-white font-medium rounded-md px-6 py-3 transition-colors"
                onClick={handleApplyClick}
              >
                Konsultasi Beasiswa Ini
              </button>
            </div> */}

            {/* CTA */}
            <div className="bg-cream/30 border border-cream rounded-lg p-6 mt-10">
              <h3 className="text-xl font-serif font-bold text-gray-900 mb-3">
                Tertarik dengan Beasiswa Ini?
              </h3>
              <p className="text-gray-600 mb-4">
                Tim #PintarPilihKuliah siap membantu Anda. Konsultasikan kebutuhan Anda dengan para ahli kami.
              </p>
              <button
                className="inline-flex items-center justify-center bg-orange-500 hover:bg-black text-white font-medium rounded-md px-6 py-3 transition-colors"
                onClick={handleApplyClick}
              >
                <svg
                  className="w-5 h-5 mr-2"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                </svg>
                Konsultasi Sekarang
              </button>
            </div>


          </div>
        </div>
      </div>
    </div>
  );
};

export default BeasiswaDetail;